import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { Tension } from '../../types';
import './AdminTensions.css';

interface TensionWithUser extends Tension {
  user_name: string;
  user_email: string;
}

const AdminTensions: React.FC = () => {
  const [tensions, setTensions] = useState<TensionWithUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadTensions();
  }, []);

  const loadTensions = async (search?: string, startDate?: string, endDate?: string) => {
    try {
      setIsLoading(true);
      const params: any = { limit: 100 };
      if (search) params.search = search;
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      const response = await adminAPI.getTensions(params);
      setTensions(response.data.data.tensions);
      setTotalCount(response.data.data.pagination.total);
    } catch (err: any) {
      setError('Error al cargar registros de tensiones');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    loadTensions(searchTerm, dateRange.startDate, dateRange.endDate);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDateRange({ startDate: '', endDate: '' });
    loadTensions();
  };

  const getTensionCategory = (systolic: number, diastolic: number) => {
    if (systolic < 120 && diastolic < 80) {
      return { label: 'Normal', color: '#10b981' };
    } else if (systolic >= 120 && systolic <= 129 && diastolic < 80) {
      return { label: 'Elevada', color: '#f59e0b' };
    } else if ((systolic >= 130 && systolic <= 139) || (diastolic >= 80 && diastolic <= 89)) {
      return { label: 'HTA Grado 1', color: '#f97316' };
    } else if (systolic >= 140 || diastolic >= 90) {
      return { label: 'HTA Grado 2', color: '#ef4444' };
    }
    return { label: 'Indeterminada', color: '#6b7280' };
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const exportToCSV = () => {
    const headers = ['Fecha', 'Hora', 'Usuario', 'Email', 'Sistolica', 'Diastolica', 'Pulso', 'Categoria', 'Notas'];
    const rows = tensions.map(t => {
      const cat = getTensionCategory(t.systolic, t.diastolic);
      return [
        t.date,
        t.time?.substring(0, 5) || '',
        t.user_name,
        t.user_email,
        t.systolic,
        t.diastolic,
        t.pulse || '',
        cat.label,
        t.notes || ''
      ];
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `tensiones_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (isLoading && tensions.length === 0) {
    return <div className="admin-loading">Cargando registros...</div>;
  }

  return (
    <div className="admin-tensions">
      <div className="admin-tensions-header">
        <h1>Todas las Tensiones</h1>
        <div className="header-stats">
          <span className="stat-badge">{totalCount} registros totales</span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="filters-container">
        <div className="search-box">
          <input
            type="text"
            placeholder="Buscar por usuario o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="search-input"
          />
        </div>
        <div className="date-filters">
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
            className="date-input"
          />
          <span>a</span>
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
            className="date-input"
          />
        </div>
        <div className="filter-actions">
          <button onClick={handleSearch} className="btn btn-primary">Buscar</button>
          <button onClick={handleClearFilters} className="btn btn-secondary">Limpiar</button>
          <button onClick={exportToCSV} className="btn btn-success">Exportar CSV</button>
        </div>
      </div>

      {tensions.length === 0 ? (
        <div className="empty-state">
          <p>No hay registros de tensiones</p>
        </div>
      ) : (
        <div className="tensions-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Hora</th>
                <th>Usuario</th>
                <th>Sistolica</th>
                <th>Diastolica</th>
                <th>Pulso</th>
                <th>Categoria</th>
                <th>Notas</th>
              </tr>
            </thead>
            <tbody>
              {tensions.map((tension) => {
                const category = getTensionCategory(tension.systolic, tension.diastolic);
                return (
                  <tr key={tension.id}>
                    <td>{formatDate(tension.date)}</td>
                    <td>{tension.time?.substring(0, 5) || '-'}</td>
                    <td>
                      <div className="user-info">
                        <span className="user-name">{tension.user_name}</span>
                        <span className="user-email">{tension.user_email}</span>
                      </div>
                    </td>
                    <td className="value-cell">
                      <span className="tension-value">{tension.systolic}</span>
                    </td>
                    <td className="value-cell">
                      <span className="tension-value">{tension.diastolic}</span>
                    </td>
                    <td className="value-cell">
                      {tension.pulse ? <span className="pulse-value">{tension.pulse}</span> : '-'}
                    </td>
                    <td>
                      <span
                        className="category-badge"
                        style={{
                          backgroundColor: `${category.color}20`,
                          color: category.color,
                          border: `1px solid ${category.color}`
                        }}
                      >
                        {category.label}
                      </span>
                    </td>
                    <td className="notes-cell">{tension.notes || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminTensions;
